import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Layout,
  Menu,
  Button,
  theme,
  Typography,
  Avatar,
  Dropdown,
  Space,
  Divider,
  Drawer,
  Grid,
} from 'antd';
import {
  DashboardOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  PlusOutlined,
  VideoCameraOutlined,
  UserOutlined,
  LogoutOutlined,
  SettingOutlined,
  BellOutlined,
  CalendarOutlined,
  CrownOutlined,
  SafetyCertificateOutlined,
  AppstoreOutlined,
  FileTextOutlined,
  VideoCameraFilled,
  DollarOutlined,
  ControlOutlined,
  ReadOutlined,
  BookOutlined,
} from '@ant-design/icons';
import { COLORS } from '../theme';
import { useAuth } from '../contexts/AuthContext';
import VrukshaEduLogo from '../components/VrukshaEduLogo';

const { Header, Sider, Content } = Layout;
const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const MainLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { adminUser, whiteLabelConfig, logout } = useAuth();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const {
    token: { borderRadiusLG },
  } = theme.useToken();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: adminUser?.displayName || 'Admin',
      disabled: true,
    },
    {
      key: 'role',
      icon: <SettingOutlined />,
      label: adminUser?.role?.replace(/_/g, ' ').toUpperCase(),
      disabled: true,
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      danger: true,
      onClick: handleLogout,
    },
  ];

  const baseKeyFromPath = (path: string) => {
    if (path.startsWith('/dashboard'))        return '/dashboard';
    if (path.startsWith('/govt-exams'))       return '/govt-exams';
    if (path.startsWith('/scheduled-tests'))  return '/scheduled-tests';
    if (path.startsWith('/live-tests'))       return '/live-tests';
    if (path.startsWith('/students'))         return '/students';
    if (path.startsWith('/notifications'))    return '/notifications';
    if (path.startsWith('/subscriptions'))    return '/subscriptions';
    if (path.startsWith('/test-series'))      return '/test-series';
    if (path.startsWith('/study-materials'))  return '/study-materials';
    if (path.startsWith('/current-affairs'))  return '/current-affairs';
    if (path.startsWith('/ncert-prep'))        return '/ncert-prep';
    if (path.startsWith('/recorded-sessions'))return '/recorded-sessions';
    if (path.startsWith('/purchases'))        return '/purchases';
    if (path.startsWith('/app-config'))       return '/app-config';
    return '/dashboard';
  };

  const menuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: 'Dashboard',
    },
    {
      key: 'divider-exams',
      type: 'divider' as const,
      label: '',
    },
    {
      key: '/govt-exams',
      icon: <SafetyCertificateOutlined />,
      label: 'Govt Exams',
    },
    {
      key: '/test-series',
      icon: <AppstoreOutlined />,
      label: 'Test Series',
    },
    {
      key: '/study-materials',
      icon: <FileTextOutlined />,
      label: 'Study Materials',
    },
    {
      key: '/current-affairs',
      icon: <ReadOutlined />,
      label: 'Current Affairs',
    },
    {
      key: '/ncert-prep',
      icon: <BookOutlined />,
      label: 'NCERT Prep',
    },
    {
      key: '/scheduled-tests',
      icon: <CalendarOutlined />,
      label: 'Scheduled Tests',
    },
    {
      key: '/live-tests',
      icon: <VideoCameraOutlined />,
      label: 'Live Tests',
    },
    {
      key: 'divider-manage',
      type: 'divider' as const,
      label: '',
    },
    {
      key: '/students',
      icon: <UserOutlined />,
      label: 'Students',
    },
    {
      key: '/recorded-sessions',
      icon: <VideoCameraFilled />,
      label: 'Recorded Classes',
    },
    {
      key: '/notifications',
      icon: <BellOutlined />,
      label: 'Notifications',
    },
    {
      key: 'divider-monetise',
      type: 'divider' as const,
      label: '',
    },
    {
      key: '/subscriptions',
      icon: <CrownOutlined />,
      label: 'Subscriptions',
    },
    {
      key: '/purchases',
      icon: <DollarOutlined />,
      label: 'Purchases & Revenue',
    },
    {
      key: '/app-config',
      icon: <ControlOutlined />,
      label: 'App Config',
    },
  ];

  const getFilteredMenuItems = () => menuItems;

  const brandColor = whiteLabelConfig?.brandColor || COLORS.primary;

  const SidebarMenu = (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div
        style={{
          height: 80,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '12px',
          borderBottom: `1px solid rgba(255, 255, 255, 0.1)`,
          background: 'rgba(0, 0, 0, 0.1)',
        }}
      >
        {whiteLabelConfig?.institutionLogo ? (
          <img
            src={whiteLabelConfig.institutionLogo}
            alt={whiteLabelConfig.institutionName}
            style={{
              maxWidth: collapsed && !isMobile ? '40px' : '120px',
              maxHeight: collapsed && !isMobile ? '40px' : '60px',
              objectFit: 'contain',
              transition: 'all 0.3s',
            }}
          />
        ) : (
          <img
            src="/nammapreplogo.png"
            alt="NammaPrep"
            style={{
              width: collapsed && !isMobile ? '40px' : '48px',
              height: collapsed && !isMobile ? '40px' : '48px',
              objectFit: 'cover',
              borderRadius: '50%',
              transition: 'all 0.3s',
            }}
          />
        )}
      </div>
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[baseKeyFromPath(location.pathname)]}
        items={getFilteredMenuItems()}
        onClick={({ key }) => { navigate(key); if (isMobile) setDrawerOpen(false); }}
        style={{ background: 'transparent', flex: 1 }}
      />
      {(!collapsed || isMobile) && (
        <div
          style={{
            padding: '16px 12px',
            textAlign: 'center',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            background: 'rgba(0, 0, 0, 0.2)',
          }}
        >
          <Text style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '10px', display: 'block', marginBottom: '6px' }}>
            Powered by Calloc Technologies Pvt Ltd
          </Text>
          <VrukshaEduLogo width={40} height={40} />
        </div>
      )}
    </div>
  );

  return (
    <Layout style={{ minHeight: '100vh', background: COLORS.backgroundGray }}>
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          theme="dark"
          style={{
            background: `linear-gradient(180deg, ${brandColor} 0%, ${COLORS.accent} 100%)`,
            position: 'sticky',
            top: 0,
            height: '100vh',
            overflow: 'auto',
          }}
        >
          {SidebarMenu}
        </Sider>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          placement="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width={240}
          styles={{
            body: { padding: 0, background: `linear-gradient(180deg, ${brandColor} 0%, ${COLORS.accent} 100%)` },
            header: { display: 'none' },
          }}
          style={{ zIndex: 1001 }}
        >
          {SidebarMenu}
        </Drawer>
      )}

      <Layout>
        <Header
          style={{
            padding: '0 16px',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${COLORS.border}`,
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            height: '64px',
            lineHeight: '64px',
            position: 'sticky',
            top: 0,
            zIndex: 100,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1, minWidth: 0 }}>
            <Button
              type="text"
              icon={isMobile ? <MenuUnfoldOutlined /> : collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => isMobile ? setDrawerOpen(true) : setCollapsed(!collapsed)}
              style={{ fontSize: '16px', width: 40, height: 40, flexShrink: 0 }}
            />
            {whiteLabelConfig?.institutionLogo && (
              <>
                <Divider type="vertical" style={{ height: '32px', margin: '0 8px' }} />
                <img
                  src={whiteLabelConfig.institutionLogo}
                  alt={whiteLabelConfig.institutionName}
                  style={{ height: '36px', maxWidth: '100px', objectFit: 'contain', flexShrink: 0 }}
                />
              </>
            )}
            <div style={{ minWidth: 0, overflow: 'hidden' }}>
              <Title
                level={5}
                style={{
                  margin: 0,
                  color: brandColor,
                  fontSize: '14px',
                  lineHeight: '20px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {whiteLabelConfig?.institutionName || 'Meritx'} Admin
              </Title>
              {adminUser && (
                <Text
                  type="secondary"
                  style={{
                    fontSize: '11px',
                    lineHeight: '16px',
                    display: 'block',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {adminUser.role.replace(/_/g, ' ').toUpperCase()}
                </Text>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
            {location.pathname === '/live-tests' && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => navigate('/live-tests/create')}
                style={{ background: brandColor, borderColor: brandColor }}
                size={isMobile ? 'small' : 'middle'}
              >
                {!isMobile && 'Create Live Test'}
              </Button>
            )}
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" arrow>
              <Button type="text" style={{ height: 40, padding: '0 8px' }}>
                <Space size={8}>
                  <Avatar size={32} icon={<UserOutlined />} style={{ background: brandColor }} />
                  {!isMobile && (
                    <Text strong style={{ fontSize: '14px' }}>
                      {adminUser?.displayName?.split(' ')[0] || 'Admin'}
                    </Text>
                  )}
                </Space>
              </Button>
            </Dropdown>
          </div>
        </Header>
        <Content
          style={{
            margin: isMobile ? '12px 8px' : '24px 16px',
            padding: isMobile ? 16 : 24,
            minHeight: 280,
            background: COLORS.background,
            borderRadius: borderRadiusLG,
            boxShadow: `0 2px 8px rgba(0,0,0,0.05)`,
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
